// Mirrors src/screens/assets/AssetsListScreen.tsx — table with
//   OEM | Model | Asset ID | Active Tag | Secondary Tags | Actions
// plus SearchBar and 4 SummaryCards (Active / Down / Maintenance / Idle).

import React, {useMemo, useState} from 'react'
import clsx from 'clsx'
import {useNavigate} from 'react-router-dom'
import {Pencil, Plus} from 'lucide-react'
import {SearchBar} from '../components/common/SearchBar'
import {SummaryCard} from '../components/common/SummaryCard'
import {TagBadge} from '../components/common/TagBadge'
import {useSupabaseSubscription} from '../app/useSupabaseSubscription'

interface EquipmentRow {
  id: string
  name: string
  model: string
  asset_id: string
  activity: string | null
  status: 'active' | 'down' | 'maintenance' | 'idle'
}

interface TagRow {
  id: string
  tag_id: string
  assigned_to_id: string | null
  assigned_to_type: string | null
  battery_level: number
  signal_strength: string
  is_connected: boolean
  created_at: string | null
}

export const EquipmentList: React.FC = () => {
  const navigate = useNavigate()
  const [search, setSearch] = useState('')

  const {rows: equipment, loading: eLoading, error: eError} =
    useSupabaseSubscription<EquipmentRow>({
      table: 'equipment',
      columns: 'id, name, model, asset_id, activity, status',
      orderBy: ['asset_id', true],
    })

  const {rows: tags, loading: tLoading, error: tError} =
    useSupabaseSubscription<TagRow>({
      table: 'tags',
      columns: 'id, tag_id, assigned_to_id, assigned_to_type, battery_level, signal_strength, is_connected, created_at',
      scopedToMine: false,
    })

  const tagsByEquipment = useMemo(() => {
    const map = new Map<string, TagRow[]>()
    for (const t of tags) {
      if (t.assigned_to_type !== 'equipment' || !t.assigned_to_id) continue
      const arr = map.get(t.assigned_to_id) ?? []
      arr.push(t)
      map.set(t.assigned_to_id, arr)
    }
    // Sort each per-equipment tag list by created_at ASC so primary =
    // tags[0] matches mobile's EquipmentRepository.getWithTags ordering.
    for (const arr of map.values()) {
      arr.sort((a, b) => {
        const at = a.created_at ? Date.parse(a.created_at) : 0
        const bt = b.created_at ? Date.parse(b.created_at) : 0
        return at - bt
      })
    }
    return map
  }, [tags])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return equipment
    return equipment.filter(
      e =>
        e.asset_id.toLowerCase().includes(q) ||
        e.name.toLowerCase().includes(q) ||
        e.model.toLowerCase().includes(q),
    )
  }, [equipment, search])

  const summary = useMemo(() => {
    const counts = {active: 0, down: 0, maintenance: 0, idle: 0}
    for (const e of equipment) counts[e.status]++
    return counts
  }, [equipment])

  if (eLoading || tLoading) {
    return <div className="flex h-full items-center justify-center text-textMuted">Loading equipment…</div>
  }
  if (eError || tError) {
    return (
      <div className="flex h-full items-center justify-center p-xxxl">
        <div className="rounded-lg border border-statusRed bg-surfaceCard p-xxl">
          <h3 className="mb-md text-lg font-semibold text-statusRed">Fetch failed</h3>
          <pre className="whitespace-pre-wrap font-mono text-sm text-textPrimary">{eError || tError}</pre>
        </div>
      </div>
    )
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col bg-background">
      {/* Toolbar — px-xl py-md gap-lg (matches mobile AssetsListScreen.controls).
          Add button: bg-primary, px-xl py-md, bold black text. */}
      <div className="px-xl py-md">
        <div className="flex items-center gap-lg">
          <div className="flex flex-1 items-center gap-lg rounded-lg bg-surface p-sm">
            <div className="flex-1">
              <SearchBar value={search} onChange={setSearch} placeholder="Search.." />
            </div>
          </div>
          <button
            type="button"
            onClick={() => navigate('/equipment/add')}
            className="flex items-center gap-sm rounded-md bg-primary px-xl py-md text-md font-bold text-black hover:bg-primaryLight"
          >
            <Plus size={16} color="black" strokeWidth={3} />
            Add Equipment
          </button>
        </div>
      </div>

      {/* Summary — px-xl gap-md mb-lg, 4 cards with colored left accents. */}
      <div className="mb-lg grid grid-cols-4 gap-md px-xl">
        <SummaryCard
          label="Active"
          value={summary.active}
          valueClass="text-statusGreen"
          accentColor="var(--color-statusGreen)"
        />
        <SummaryCard
          label="Down"
          value={summary.down}
          valueClass="text-statusRed"
          accentColor="var(--color-statusRed)"
        />
        <SummaryCard
          label="Maintenance"
          value={summary.maintenance}
          valueClass="text-statusYellow"
          accentColor="var(--color-statusYellow)"
        />
        <SummaryCard
          label="Idle"
          value={summary.idle}
          accentColor="var(--color-textMuted)"
        />
      </div>

      {/* Table — surfaceCard rounded-lg border-surfaceBorder mx-xl mb-xl.
          Columns from mobile AssetsListScreen:
            oem 1fr / model 0.8 / asset_id 0.8 / active_tag 1 / sec_tags 1.2 / action 60px. */}
      <div className="mx-xl mb-xl flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-surfaceBorder bg-surfaceCard">
        <div className="grid shrink-0 grid-cols-[1fr_0.8fr_0.8fr_1fr_1.2fr_60px] items-center border-b border-divider bg-surface px-lg py-md text-xs font-semibold uppercase text-textMuted">
          <div>OEM</div>
          <div>Model</div>
          <div>Asset ID</div>
          <div>Active Tag</div>
          <div>Secondary Tags</div>
          <div className="text-center">Actions</div>
        </div>
        <div className="flex-1 overflow-y-auto">
          {filtered.map(e => {
            const eqTags = tagsByEquipment.get(e.id) ?? []
            const primary = eqTags[0]
            const others = eqTags.slice(1)
            return (
              <button
                key={e.id}
                type="button"
                onClick={() => navigate(`/equipment/${e.id}`)}
                className={clsx(
                  'grid w-full grid-cols-[1fr_0.8fr_0.8fr_1fr_1.2fr_60px] items-center px-lg py-md text-left',
                  'border-b border-divider hover:bg-surfaceLight/40 focus:bg-surfaceLight/60 focus:outline-none',
                )}
              >
                <div className="truncate text-md font-semibold text-textPrimary">{e.name}</div>
                <div className="truncate text-md font-semibold text-textPrimary">{e.model}</div>
                <div className="truncate text-md font-semibold text-textPrimary">{e.asset_id}</div>
                <div>
                  {primary ? (
                    <TagBadge tagId={primary.tag_id} isActive={primary.is_connected} />
                  ) : (
                    <span className="text-sm italic text-textMuted">—</span>
                  )}
                </div>
                <div className="flex flex-wrap gap-xs">
                  {others.length === 0 ? (
                    <span className="text-sm italic text-textMuted">—</span>
                  ) : (
                    others.map(t => (
                      <TagBadge key={t.id} tagId={t.tag_id} isActive={t.is_connected} />
                    ))
                  )}
                </div>
                <div className="flex justify-center">
                  <span
                    role="button"
                    tabIndex={-1}
                    onClick={ev => {
                      ev.stopPropagation()
                      navigate(`/equipment/${e.id}/edit`)
                    }}
                    className="flex h-9 w-9 items-center justify-center rounded-md border border-surfaceBorder text-textPrimary hover:border-primary hover:text-primary"
                    aria-label={`Edit ${e.asset_id}`}
                  >
                    <Pencil size={14} />
                  </span>
                </div>
              </button>
            )
          })}
          {filtered.length === 0 && (
            <div className="px-lg py-xxxl text-center text-textMuted">
              No equipment matches "{search}".
            </div>
          )}
        </div>
      </div>
      <div className="h-md" />
    </div>
  )
}
